/* eslint-disable @typescript-eslint/no-explicit-any */
import { GroupItens, User } from "src/@types/user";
import { apiSlice } from "./http";
import { Tags } from "@/enum/tags";
import { Paginated } from "@/@types/paginated";
import { readBlobAsDataUrl } from "@/utils/read-blob-as-data-url";

interface IUserParams {
  search: string;
  orderBy: string;
  perPage: number;
  meta: {
    page: number;
  };
}

export const userInformations = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    me: builder.query<User, void>({
      query: () => ({
        url: "/auth/me",
        method: "GET",
      }),
      providesTags: [Tags.USER],
    }),
    // photoUrl chega relativo do /auth/me ("user/<id>/photo"): o fetchBaseQuery prefixa a
    // VITE_REACT_API_URL inteira, incluindo o sub-path do gateway.
    userPhoto: builder.query<string, string>({
      query: (photoUrl: string) => ({
        url: photoUrl,
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
      transformResponse: (blob: Blob): Promise<string> =>
        readBlobAsDataUrl(blob),
      providesTags: [Tags.USER_PHOTO],
    }),
    roles: builder.query<User, void>({
      query: () => ({
        url: "/user/search/roles",
        method: "GET",
      }),
    }),
    updateUser: builder.mutation<
      string,
      { treasury_id: string; version: number; id: string; blocked: boolean }
    >({
      query: (user) => ({
        url: `/user/${user.id}`,
        method: "PUT",
        body: {
          treasury_id: user?.treasury_id,
          version: user?.version,
          blocked: user?.blocked,
        },
      }),
      invalidatesTags: [Tags.ALL_USERS, Tags.ONE_USER],
    }),
    updateUserRole: builder.mutation<
      string,
      { user_id: string; group_id: string; version: string; role_id: string }
    >({
      query: (user) => ({
        url: `/user/${user.user_id}/group/${user.group_id}`,
        method: "PUT",
        body: { role_id: user?.role_id, version: user?.version },
      }),
      invalidatesTags: [Tags.ALL_USERS, Tags.ONE_USER],
    }),
    updateUserRoleBatch: builder.mutation<
      string,
      { user_id: string; body: { group_id: string; version: string; role_id: string }[] }
    >({
      query: ({ user_id, body }) => ({
        url: `/user/${user_id}/batch/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [Tags.ALL_USERS, Tags.ONE_USER],
    }),

    users: builder.query<Paginated<{ meta: any; data: any[] }>, IUserParams>({
      query: ({ search, meta, orderBy, perPage }) => {
        const params = new URLSearchParams({
          search,
          orderBy,
          page: meta.page.toString(),
          perPage: perPage.toString(),
        });

        return {
          url: `/user?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.ALL_USERS],
    }),
    getOneUser: builder.query<any, { id: string }>({
      query: ({ id }) => {
        return {
          url: `/user/${id.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.ONE_USER],
    }),
    operatorMe: builder.query<User, void>({
      query: () => ({
        url: "/auth/operator/me",
        method: "GET",
        providesTags: Tags.USER,
      }),
    }),
    operatorMePublic: builder.query<User, void>({
      query: () => ({
        url: "/public/auth/operator/me",
        method: "GET",
        providesTags: Tags.USER,
      }),
    }),
    groups: builder.query<GroupItens, void>({
      query: () => ({
        url: "/user/search/groups",
        method: "GET",
      }),
      providesTags: [Tags.USER, Tags.GROUPS, Tags.ONE_GROUP],
    }),
    createUser: builder.mutation<string, { email: string; name: string }>({
      query: (user) => ({
        url: "/user",
        method: "POST",
        body: user,
        invalidatesTags: [Tags.USER],
      }),
    }),
  }),
});

export const {
  useMeQuery,
  useUserPhotoQuery,
  useOperatorMeQuery,
  useOperatorMePublicQuery,
  useLazyOperatorMeQuery,
  useCreateUserMutation,
  useGroupsQuery,
  useUsersQuery,
  useGetOneUserQuery,
  useRolesQuery,
  useUpdateUserMutation,
  useUpdateUserRoleMutation,
  useUpdateUserRoleBatchMutation,
} = userInformations;

export const {
  endpoints: {
    me: { select: selectMe },
    userPhoto: { select: selectUserPhoto },
    operatorMe: { select: selectOperatorMe },
    createUser: { select: selectCreateUser },
    groups: { select: selectGroups },
    users: { select: selectUsers },
    getOneUser: { select: selectGetOneUser },
    roles: { select: selectRoles },
    updateUser: { select: selectUpdateUser },
    updateUserRole: { select: selectUpdateUserRole },
    updateUserRoleBatch: { select: selectUpdateUserRoleBatch },
  },
} = userInformations;
